import { FC } from 'react';
import {
    AutocompleteInput,
    Datagrid,
    DatagridProps,
    DateField,
    DateInput,
    List,
    ListContextProvider,
    ListProps,
    NumberField,
    ReferenceInput,
    TextField,
    TextInput,
    useListContext,
    ReferenceField,
    SelectInput,
    NumberInput,
    ChipField,
    Pagination,
    PaginationProps,
    // FunctionField,
} from 'react-admin';

import { makeStyles } from '@material-ui/core/styles';
import {
    CurrencyFormatInput,
    dateLocales,
    PercentFormatInput,
    percentFormatter,
    currencyFormatter,
    defaultDateOptions
} from '../utils/formatters';
import _ from "lodash";

import { Box } from '@material-ui/core';
import getCountries from '../utils/countries'
import ListActions from '../components/ToolbarActions';
import CustomNumberField from '../components/customNumber';
import ColoredChipField from '../components/ColoredChip';
import BulkActionButtons from '../components/BulkActions';
import { getFinancingStates, getFinancingTypes } from './FinancingTypes';

const countries = getCountries()
const financingTypes = getFinancingTypes()
const financingStates = getFinancingStates()

const listCurrencies = Object.keys(_.groupBy(countries, c => c.code)).map(k => ({ name: k, id: k }))
const financingFilters = [
    <ReferenceInput
        source="merchant_id"
        reference="Merchant"
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
        perPage={10}
        filterToQuery={name => ({ name })}
    >
        <AutocompleteInput
            source="name"
            resettable
        />
    </ReferenceInput>,
    <ReferenceInput
        source="platform_id"
        reference="Platform"
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
        perPage={10}
        filterToQuery={name => ({ name })}
    >
        <SelectInput optionText="name" resettable />
    </ReferenceInput>,
    <NumberInput
        source="id"
        alwaysOn
    />,
    //  <SearchInput source="name" alwaysOn />,
    <DateInput source="created_at@_gte" label="resources.common.fields.created_at@_gte" />,
    <DateInput source="created_at@_lte" label="resources.common.fields.created_at@_lte" />,
    <TextInput
        source="amount@_gte"
        InputProps={{
            inputComponent: CurrencyFormatInput,
        }}
    />,
    <TextInput
        source="amount@_lte"
        InputProps={{
            inputComponent: CurrencyFormatInput,
        }}
    />,
    <SelectInput source="country" choices={countries} />,
    <SelectInput source="type" choices={financingTypes} />,
    <SelectInput source="state" choices={financingStates} />,
    <TextInput
        source="fixed_fee"
        format={(v: number) => v / 100}
        parse={(v: string) => parseFloat(v) * 100}
        InputProps={{
            inputComponent: PercentFormatInput,
        }}

    />,
    <TextInput
        source="repayment_rate"
        format={(v: number) => v / 100}
        parse={(v: string) => parseFloat(v) * 100}
        InputProps={{
            inputComponent: PercentFormatInput,
        }}
    />,
    <SelectInput source="currency" choices={listCurrencies} />,
]

const useDatagridStyles = makeStyles({
    amount: {},
    fixed_fee: {}
});

interface TabbedDatagridProps extends DatagridProps { }

const ListDatagrid: FC<TabbedDatagridProps> = props => {
    const listContext = useListContext();
    // const { ids, filterValues, setFilters, displayedFilters } = listContext;
    const classes = useDatagridStyles();


    return (
        <ListContextProvider
            value={{ ...listContext }}
        >
            <Datagrid {...props} optimized rowClick="edit">
                <NumberField source="id" label="resources.common.fields.id" />
                <DateField source="created_at" label="resources.common.fields.created_at" locales={dateLocales} options={defaultDateOptions} />
                <DateField source="updated_at" label="resources.common.fields.updated_at" locales={dateLocales} options={defaultDateOptions} />
                <CustomNumberField
                    source="amount"
                    divisor={100}
                    className={classes.amount}
                    locales="en-US"
                    options={currencyFormatter().resolvedOptions()}
                />
                <ReferenceField source="merchant_id" reference="Merchant">
                    <TextField source="name" />
                </ReferenceField>
                <ReferenceField source="platform_id" reference="Platform">
                    <ColoredChipField size="small" source="name" />
                </ReferenceField>
                <ChipField source="country" variant="outlined" size="small" color="primary" />
                <TextField source="currency" />
                <CustomNumberField
                    source="fixed_fee"
                    divisor={10000}
                    className={classes.amount}
                    locales="en-US"
                    options={percentFormatter().resolvedOptions()}
                />
                <CustomNumberField
                    source="repayment_rate"
                    divisor={10000}
                    className={classes.amount}
                    locales="en-US"
                    options={percentFormatter().resolvedOptions()}
                />
                <DateField source="financing_date" locales={dateLocales} options={defaultDateOptions} />
                <DateField source="limit_date" locales={dateLocales} options={defaultDateOptions} />
                <ColoredChipField source="state" size="small" variant="outlined" choices={financingStates} />
                <ColoredChipField source="type" size="small" choices={financingTypes} />
            </Datagrid>
        </ListContextProvider>
    );
};

const FinancingList: FC<ListProps> = props => {
    const CustomPagination = (props: JSX.IntrinsicAttributes & PaginationProps) =>
        <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;

    return (
        <List
            {...props}
            component={Box}
            actions={<ListActions />}
            bulkActionButtons={<BulkActionButtons />}
            sort={{ field: 'created_at', order: 'DESC' }}
            pagination={<CustomPagination />}
            filters={financingFilters}
            perPage={50}
        >
            <ListDatagrid />
        </List>
    )
}

export default FinancingList;
